import { useEffect, useRef } from 'react';
import { useCall } from '../context/CallContext';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, RefreshCw, ShieldCheck } from 'lucide-react';

export function CallOverlay() {
  const {
    callState, callType, remoteUser, duration,
    localStream, remoteStream, isMuted, isVideoOff,
    networkQuality, answerCall, declineCall, hangupCall,
    toggleMute, toggleVideo, flipCamera
  } = useCall();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);

  // Attach media streams to audio & video elements
  useEffect(() => {
    if (remoteAudioRef.current && remoteStream) {
      remoteAudioRef.current.srcObject = remoteStream;
      remoteAudioRef.current.play().catch(err => {
        console.warn('[CallOverlay] Remote audio autoplay error:', err);
      });
    }
  }, [remoteStream, callState]);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, callState]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
      remoteVideoRef.current.play().catch(err => {
        console.warn('[CallOverlay] Remote video play error:', err);
      });
    }
  }, [remoteStream, callState]);

  if (callState === 'idle') return null;

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const imageRoot = import.meta.env.VITE_IMAGE_ROOT || 'https://shopaxo.in';
  const callerAvatarUrl = remoteUser?.avatar ? (remoteUser.avatar.startsWith('http') ? remoteUser.avatar : imageRoot + remoteUser.avatar) : null;
  const initial = remoteUser?.name ? remoteUser.name[0].toUpperCase() : 'U';

  return (
    <div className="call-overlay-backdrop">
      <style>{`
        .call-overlay-backdrop {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(8, 12, 22, 0.82);
          backdrop-filter: blur(16px);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: system-ui, -apple-system, sans-serif;
          color: #fff;
          padding: 0;
          box-sizing: border-box;
        }
        .call-overlay-card {
          position: relative;
          width: 100%;
          height: 100%;
          background: linear-gradient(180deg, #111827 0%, #090d16 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          padding: 24px 20px;
          box-sizing: border-box;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .call-overlay-backdrop {
            padding: 24px;
          }
          .call-overlay-card {
            width: 90% !important;
            max-width: 860px !important;
            height: 85vh !important;
            max-height: 620px !important;
            border-radius: 24px !important;
            border: 1px solid rgba(255, 255, 255, 0.15) !important;
            box-shadow: 0 25px 60px rgba(0, 0, 0, 0.75) !important;
          }
          .remote-video-el {
            object-fit: contain !important;
            background: #000 !important;
          }
        }
        @media (max-width: 767px) {
          .remote-video-el {
            object-fit: cover !important;
          }
        }
        .call-action-btn {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .call-action-btn:hover {
          transform: scale(1.08);
          filter: brightness(1.15);
        }
        .call-action-btn:active {
          transform: scale(0.95);
        }
      `}</style>

      <div className="call-overlay-card">
        {/* Hidden Audio element for remote WebRTC voice audio stream */}
        <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />

        {/* ── Top Header / Call Details ── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginTop: '12px', zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', background: 'rgba(255,255,255,0.1)', padding: '4px 12px', borderRadius: '20px' }}>
            <ShieldCheck size={14} color="#10B981" />
            <span>End-to-End Encrypted {callType} Call</span>
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, margin: '6px 0 0 0', color: '#fff' }}>{remoteUser?.name || 'User'}</h2>
          <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
            {callState === 'outgoing' && 'Calling...'}
            {callState === 'incoming' && `Incoming ${callType} Call`}
            {callState === 'connecting' && 'Connecting...'}
            {callState === 'connected' && formatDuration(duration)}
            {callState === 'ended' && 'Call Ended'}
          </div>
          {callState === 'connected' && (
            <div style={{ fontSize: '11px', color: '#10B981', background: 'rgba(16, 185, 129, 0.15)', padding: '2px 8px', borderRadius: '10px' }}>
              ● {networkQuality} Connection
            </div>
          )}
        </div>

        {/* ── Center Stage (Video / Voice Avatar) ── */}
        <div style={{ position: 'relative', width: '100%', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>

          {/* Video Call Active Stage */}
          {callType === 'Video' && callState === 'connected' ? (
            <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: '20px', overflow: 'hidden', background: '#000' }}>
              {/* Remote Video Track */}
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="remote-video-el"
                style={{ width: '100%', height: '100%' }}
              />

              {/* Local Video PIP Thumbnail */}
              <div style={{
                position: 'absolute',
                bottom: '16px',
                right: '16px',
                width: '120px',
                height: '160px',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                border: '2px solid rgba(255,255,255,0.35)',
                background: '#1e293b'
              }}>
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
                />
              </div>
            </div>
          ) : (
            /* Voice Call or Outgoing/Incoming Avatar View */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{
                position: 'relative',
                width: '140px',
                height: '140px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: callState === 'outgoing' || callState === 'incoming' ? '0 0 40px rgba(99, 102, 241, 0.6)' : '0 10px 30px rgba(0,0,0,0.5)',
                border: '4px solid rgba(255,255,255,0.2)'
              }}>
                {callerAvatarUrl ? (
                  <img src={callerAvatarUrl} alt={remoteUser?.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: '56px', fontWeight: 700, color: '#fff' }}>{initial}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Bottom Call Actions Bar ── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          padding: '12px 24px',
          borderRadius: '40px',
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          marginBottom: '10px',
          zIndex: 10
        }}>

          {/* Incoming Call Accept/Reject Buttons */}
          {callState === 'incoming' ? (
            <>
              <button
                className="call-action-btn"
                onClick={declineCall}
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: '#EF4444',
                  border: 'none',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(239, 68, 68, 0.4)'
                }}
              >
                <PhoneOff size={26} />
              </button>

              <button
                className="call-action-btn"
                onClick={answerCall}
                style={{
                  width: '68px',
                  height: '68px',
                  borderRadius: '50%',
                  background: '#10B981',
                  border: 'none',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 8px 28px rgba(16, 185, 129, 0.5)'
                }}
              >
                <Phone size={30} />
              </button>
            </>
          ) : (
            /* Active or Outgoing Call Action Toolbar */
            <>
              {/* Mute Mic */}
              <button
                className="call-action-btn"
                onClick={toggleMute}
                title={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: isMuted ? '#EF4444' : 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                {isMuted ? <MicOff size={22} /> : <Mic size={22} />}
              </button>

              {/* Video Disable Toggle (For Video Calls) */}
              {callType === 'Video' && (
                <button
                  className="call-action-btn"
                  onClick={toggleVideo}
                  title={isVideoOff ? 'Turn On Camera' : 'Turn Off Camera'}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: isVideoOff ? '#EF4444' : 'rgba(255,255,255,0.15)',
                    border: 'none',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  {isVideoOff ? <VideoOff size={22} /> : <Video size={22} />}
                </button>
              )}

              {/* Flip Camera (For Video Calls) */}
              {callType === 'Video' && (
                <button
                  className="call-action-btn"
                  onClick={flipCamera}
                  title="Switch Camera"
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <RefreshCw size={22} />
                </button>
              )}

              {/* End Call Button */}
              <button
                className="call-action-btn"
                onClick={hangupCall}
                title="End Call"
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: '#EF4444',
                  border: 'none',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(239, 68, 68, 0.4)'
                }}
              >
                <PhoneOff size={26} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
